import { useContext, useEffect } from 'react';

import { FlagsContext } from 'src/context/flagsContext';
import { isElectron } from 'src/hooks/useElectron';
import { useAppSelector } from 'src/hooks/useStore';
import { selectCurrentUser } from 'src/store/storeUtils';

const ASSEMBLY_PROTOCOL = 'assembly';

/** time to wait before removing the iframe (in ms) */
const REDIRECT_TIMEOUT_MS = 2000;

/**
 * Redirects to the Desktop app (if the user has enabled the setting)
 * by attempting to open a deep link to the current page. If the app is not installed,
 * the browser will simply ignore the request and stay on the current page.
 */
export const useDesktopAppRedirect = (): void => {
  const { fields: { openDeepLinksInDesktopApp = false } = {} } =
    useAppSelector(selectCurrentUser) ?? {};
  const { EnableDesktopAppAutolaunch } = useContext(FlagsContext);

  useEffect(() => {
    if (!EnableDesktopAppAutolaunch) return;

    // per-user setting to enable desktop app autolaunch, stored in user attributes
    if (!openDeepLinksInDesktopApp) return;

    // this isElectron check is extra since we've already gated the component,
    // but I want to be extra sure that this doesn't run in Electron
    if (isElectron()) return;

    const { pathname, search, hash } = window.location;

    // creating a hidden iframe so the browser will try to open the deep link,
    // but won't navigate away from the current page if the app isn't installed
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${ASSEMBLY_PROTOCOL}://${pathname.replace(/^\/+/, '')}${search}${hash}`;
    document.body.appendChild(iframe);

    const timer = setTimeout(() => iframe.remove(), REDIRECT_TIMEOUT_MS);

    return () => {
      clearTimeout(timer);
      iframe.remove();
    };
  }, [EnableDesktopAppAutolaunch, openDeepLinksInDesktopApp]);
};
